import React, { useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import styles from './styles.module.scss'
import { useTranslation } from 'react-i18next'
import { checkNested } from 'srcReact/helpers/validator'
import ReactTable from 'srcReact/components/common2/ReactTable'
import { organizationSettingsActions } from 'srcReact/OrganizationSettingsPage'
import ReactMenu from 'srcReact/components/common2/ReactMenu'
import { getWorkplaceRoleColor, getWorkplaceMemberSince } from 'srcReact/helpers/utils'
import DesktopHeader from '../DesktopHeader'
import Avatar from 'srcReact/components/common2/Avatar'
import { getCurrentOrgId, isOrganizationManager } from 'srcReact/helpers/organizationHelper'
import LightProfileModal from 'srcReact/components/common2/LightProfileModal'

const LeadershipTeam = () => {
  const { t } = useTranslation()

  const dispatch = useDispatch()
  const {
    login: { user: loggedInUser },
    common: { isMobile },
    organization: { current_organization },
    organizationSettings: { leadership_team, loading, refresh }
  } = useSelector((state) => state)
  const [orderby] = useState('lastname')
  const [modalData, setModalData] = useState({
    isShowUserModal: false,
    selectedModalUser: {}
  })

  useEffect(() => {
    if (checkNested(current_organization, 'info', 'id')) {
      dispatch(organizationSettingsActions.getLeadershipTeam(current_organization.info.id, orderby))
    }
  }, [current_organization && current_organization.info && current_organization.info.id, refresh])

  const handleUpdateMemberRole = (row, action) => {
    let payload = { resourceKey: row.user_id, action, roles: 'workplace-manager' }
    let workplaces = row.workplaces.map((w) => w.id)
    dispatch(organizationSettingsActions.updateMemberRole(workplaces.toString(), payload, 'leadership'))
  }
  const options = (row) => {
    let role = row.role.split(',')[0]
    let isLoggedInUser = row.user_id === loggedInUser.id
    let actions = []
    actions.push({
      text: t('organizationSettings.leadershipTeam.actions.viewProfile'),
      onClick: () => showUserModal(row)
    })
    if (!isLoggedInUser && role === 'workplace-manager') {
      actions.push({
        text: t('organizationSettings.leadershipTeam.actions.removeRole'),
        onClick: () => handleUpdateMemberRole(row, 'remove')
      })
    }
    actions.push({
      text: t('organizationSettings.leadershipTeam.actions.sendMessage'),
      onClick: () => showUserModal(row, 'messenger')
    })
    const orgActions = getOrgManagerActions(row)
    if (orgActions.length > 0) {
      actions = actions.concat(orgActions)
    }
    return actions
  }

  const getOptionsBasedOnRole = () => {
    let options = getOrgManagerActions(modalData.selectedModalUser)
    return options
  }

  const getOrgManagerActions = (user) => {
    let options = []
    let isLoggedInUser = user.user_id === loggedInUser.id
    if (isOrganizationManager(current_organization) && !isLoggedInUser) {
      if (user.role?.includes('account_owner')) {
        options.push({
          text: t('organizationSettings.members.actions.removeOrgAdmin'),
          onClick: () => updateOrgRole(user.user_id, 'account_owner', 'remove')
        })
      } else {
        options.push({
          text: t('organizationSettings.members.actions.makeOrgAdmin'),
          onClick: () => updateOrgRole(user.user_id, 'account_owner', 'add')
        })
      }
      if (user.role?.includes('technical_support')) {
        options.push({
          text: t('organizationSettings.members.actions.removeITSupport'),
          onClick: () => updateOrgRole(user.user_id, 'technical_support', 'remove')
        })
      } else {
        options.push({
          text: t('organizationSettings.members.actions.makeITSupport'),
          onClick: () => updateOrgRole(user.user_id, 'technical_support', 'add')
        })
      }
    }
    return options
  }

  const showUserModal = (user, screen = 'main') => {
    setModalData({
      isShowUserModal: true,
      selectedModalUser: user,
      screen: screen
    })
  }

  const hideUserModal = () => {
    setModalData({
      ...modalData,
      isShowUserModal: false
    })
  }

  const updateOrgRole = (userId, role, action) => {
    const orgId = getCurrentOrgId()
    dispatch(organizationSettingsActions.updateOrgRole(orgId, userId, role, action, 'leadership'))
  }
  const columns = useMemo(
    () => [
      {
        Header: t('organizationSettings.leadershipTeam.columns.member'),
        id: 'name',
        accessor: (row, idx) => (
          <div role='button' className={styles.name} onClick={() => showUserModal(row)}>
            <Avatar
              imgWrapperSize='tiny'
              userId={row.user_id}
              profilePicture={row.profile_picture}
              firstname={row.firstname}
              lastname={row.lastname}
              mimeType={row.mime_type}
              originalTag={row.original_tag}
              isOriginal={row.is_original}
              containerCSS={styles.picture}
              showImageAsDecorative={true}
              withExpertiseGroup={row.with_expertise_group}
            />
            <div>{row.firstname + ' ' + row.lastname}</div>
          </div>
        )
      },
      {
        Header: t('organizationSettings.leadershipTeam.columns.role'),
        id: 'role',
        accessor: (row, idx) => {
          const roles = row.role.split(',')
          const rolesText = roles.map((role) => t(`global.roles.${role.replace('-', '_')}`)).join(', ')
          return (
            <div className={styles.role} style={{ backgroundColor: getWorkplaceRoleColor(row.role) }}>
              {rolesText}
            </div>
          )
        }
      },
      {
        Header: t('organizationSettings.leadershipTeam.columns.workplaceName'),
        id: 'workplaces',
        accessor: (row, idx) => (
          <div className={styles.workplaceName}>
            {row.workplaces.map((w, i, arr) => `${w.name}${arr.length - 1 === i ? '' : ','}`)}
          </div>
        ),
        className: styles.rowWorkplaceName
      },
      {
        Header: t('organizationSettings.leadershipTeam.columns.memberSince'),
        id: 'member_since',
        accessor: (row, idx) => <div className={styles.memberSince}>{getWorkplaceMemberSince(row.member_since)}</div>,
        className: styles.rowMemberSince
      },
      {
        Header: t('organizationSettings.leadershipTeam.columns.email'),
        accessor: 'email',
        className: styles.rowEmail
      },
      {
        Header: '',
        id: 'options',
        accessor: (row, idx) => <ReactMenu options={options(row)} />
      }
    ],
    []
  )

  return (
    <div className={styles.leadershipTeamWrapper}>
      {!isMobile && (
        <DesktopHeader
          title={t('organizationSettings.leadershipTeam.title')}
          subtitle={t('organizationSettings.leadershipTeam.subtitle')}
        />
      )}

      <ReactTable columns={columns} data={leadership_team} className={styles.tableClassName} loading={loading} />
      {modalData.isShowUserModal && (
        <LightProfileModal
          show={modalData.isShowUserModal}
          userIdwithRole={modalData.selectedModalUser.user_id}
          roleManagementOptions={getOptionsBasedOnRole()}
          closeModalWithRole={hideUserModal}
          screenWithRole={modalData.screen}
        />
      )}
    </div>
  )
}

export default LeadershipTeam
